"use client";
import { useEffect } from "react";
import { Home, ArrowLeft } from "lucide-react";
import { Header } from "@/components/Header";

export default function NotFoundPage() {
  // Set page title and meta description
  useEffect(() => {
    document.title = "404 - Page Not Found | Korrero";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'The page you are looking for does not exist.');
    }
  }, []);
  return (
    <div className="min-h-screen relative text-theme-primary gradient-container transition-colors duration-150 ease-in-out">
      <div className="bg-main"></div>

      {/* Header */}
      <Header showFixed={false} />

      {/* 404 Content */}
      <section className="px-4 sm:px-6 md:px-9 pt-24 md:pt-48 pb-8 md:pb-12 text-center">
        {/* 404 Number */}
        <div className="mb-8">
          <h1 className="text-8xl sm:text-9xl md:text-[12rem] font-bold bg-gradient-to-r from-[var(--color-primary-main)] to-[var(--color-primary-light)] bg-clip-text text-transparent leading-none">
            404
          </h1>
        </div>

        {/* Error Message */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium mb-4 max-w-2xl mx-auto leading-tight">
            Oops! Page Not Found
          </h2>
          <p className="text-base sm:text-lg text-theme-secondary max-w-2xl mx-auto leading-relaxed">
            The page you're looking for doesn't exist or has been moved. 
            Let's get you back on track.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-3 px-4 sm:px-0">
          <a
            href="/"
            className="px-6 sm:px-8 py-3 sm:py-4 bg-accent text-[var(--color-text-inverse-default)] rounded-full hover:bg-primary hover:text-white transition-colors duration-300 ease-in-out text-base sm:text-lg font-medium w-full sm:w-auto inline-flex items-center justify-center"
          >
            <Home className="w-5 h-5 mr-2" />
            Go Home
          </a>
          <button
            onClick={() => window.history.back()}
            className="px-6 sm:px-8 py-3 sm:py-4 border border-theme text-theme-primary rounded-full hover:bg-primary hover:text-white hover:border-white/20 transition-colors duration-300 ease-in-out text-base sm:text-lg font-medium w-full sm:w-auto inline-flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Go Back
          </button>
        </div>
      </section>

      {/* Help Section - Bottom of page */}
      <section className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 md:px-9 pb-8 md:pb-12">
        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-tr from-[var(--color-primary-main)]/0 to-[var(--color-primary-main)]/4 backdrop-blur-sm border border-theme rounded-3xl p-6 sm:p-8 text-center">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-medium mb-3">Need Help?</h3>
            <p className="text-sm text-theme-secondary mb-4">
              Can't find what you're looking for? Try these helpful links:
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://console.korrero.com"
                className="text-sm text-primary hover:text-primary-light transition-colors group relative inline-flex items-center"
              >
                <span>Console Dashboard</span>
                <span className="absolute -top-1 -right-3 text-[10px] text-theme-secondary opacity-50 transition-opacity group-hover:opacity-100 group-hover:text-primary">
                  ↗
                </span>
              </a>
              <span className="hidden sm:inline text-theme-secondary">•</span>
              <a
                href="https://docs.korrero.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:text-primary-light transition-colors group relative inline-flex items-center"
              >
                <span>Documentation</span>
                <span className="absolute -top-1 -right-3 text-[10px] text-theme-secondary opacity-50 transition-opacity group-hover:opacity-100 group-hover:text-primary">
                  ↗
                </span>
              </a>
              <span className="hidden sm:inline text-theme-secondary">•</span>
              <a
                href="mailto:support@korrero.com"
                className="text-sm text-primary hover:text-primary-light transition-colors relative inline-flex items-center"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}